import AdvisorOnboarding from "Pages/AdvisorOnboarding";
import ChangePasswordModal from "Pages/Authentication/ChangePasswordModal";
import axios from "axios";
import {
  AddUser1Icon,
  Avatar,
  Box,
  ClickAwayListener,
  CustomeBreadCrumbs,
  Fade,
  Grid2,
  IconButton,
  NotesIcon,
  Popper,
  ProfileMenu,
  Show,
  SideMenu,
  SimpleLoader,
  Tooltip,
  Typography,
} from "components";
import { motion } from "framer-motion";
import { useDashboardLayout } from "hooks/useDashboardLayout";
import { useMutation } from "hooks/useQueries";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SIDE_BAR_OPEN } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { profileBorderColorRoleWise } from "utils/colorHelper";
import { UserRoleTypes } from "utils/common";

interface DashboardLayoutProps {
  title: string;
  loading?: boolean;
  onlyChildren?: boolean;
  children?: React.ReactNode;
  toolbar?: React.ReactNode;
  disablePadding?: boolean;
}

const DashboardLayout = (props: DashboardLayoutProps) => {
  const {
    toolbar,
    children,
    loading,
    onlyChildren = false,
    disablePadding = false,
  } = props;

  const sidebarOpen = useAppSelector((state) => state?.app?.sidebarOpen);
  const documentTitle = useAppSelector((state) => state?.app?.documentTitle);
  const userInfo = useAppSelector((state) => state?.user?.userData);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { items } = useDashboardLayout();

  const [openAdvisorAddModal, setOpenAdvisorAddModal] = useState(false);

  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { mutate: handleGetOPT, isLoading: otpLoading } = useMutation({
    mutationFn: () =>
      axios.post(
        `${process.env.REACT_APP_BASE_URL}/otp/${userInfo?.countryCode}-${userInfo?.phone}/send_otp/`,
        {
          email: userInfo?.email,
        },
      ),
    onSuccess: () => {
      setOpenPasswordModal(true);
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Show.When isTrue={onlyChildren}>{children}</Show.When>
      <Show.When isTrue={!onlyChildren}>
        <Grid2 container position={"relative"}>
          <Grid2
            size={12}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            px={1}
            position={"sticky"}
            top={0}
            maxWidth={"100vw"}
            zIndex={1}
            height={"66px"}
            sx={{
              background: "var(--white)",
              borderBottom: "solid 1px",
              borderColor: "var(--neutral-100)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 15,
                height: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: "233px",
                  height: "100%",
                  borderRight: `1px solid var(--neutral-100)`,
                }}
              >
                <IconButton
                  className="outline"
                  onClick={() => dispatch(SIDE_BAR_OPEN(!sidebarOpen))}
                >
                  <img
                    src={
                      sidebarOpen
                        ? "/LandingPage/expandLess.svg"
                        : "/LandingPage/expandMore.svg"
                    }
                    alt=""
                  />
                </IconButton>
                <img
                  src={userInfo?.companyLogo || "/Logos/iconFullLogo.png"}
                  alt=""
                  style={{
                    transition: "all .4s linear",
                    padding: 16,
                    maxWidth: 174,
                    maxHeight: "-webkit-fill-available",
                  }}
                  draggable="false"
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography fontWeight={600}>
                  {documentTitle || "Dashboard"}
                </Typography>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <CustomeBreadCrumbs />

              <span>{toolbar}</span>
              {userInfo?.grantAccess && (
                <Tooltip title="Notes" placement="bottom" arrow>
                  <IconButton
                    className="outline"
                    style={{
                      borderRadius: 10,
                    }}
                    onClick={() => {
                      navigate("/app/advice-register-notes");
                    }}
                  >
                    <NotesIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Show.When
                isTrue={
                  userInfo?.userRole !== UserRoleTypes.client &&
                  userInfo?.grantAccess
                }
              >
                <Tooltip title="Client Onboarding" placement="bottom" arrow>
                  <IconButton
                    className="outline"
                    style={{
                      borderRadius: 10,
                    }}
                    onClick={() => {
                      setOpenAdvisorAddModal(true);
                    }}
                  >
                    <AddUser1Icon />{" "}
                  </IconButton>
                </Tooltip>
              </Show.When>

              {/* <Show.When isTrue={userInfo?.userRole !== UserRoleTypes.client}>
                <Tooltip title="Ticket" placement="bottom" arrow>
                  <IconButton
                    className="outline"
                    style={{
                      borderRadius: 10,
                    }}
                    onClick={() => {
                      navigate("/app/ticket");
                    }}
                  >
                    <Ticket />{" "}
                  </IconButton>
                </Tooltip>
              </Show.When> */}

              <IconButton className="outline" onClick={handleClick}>
                <Avatar
                  alt=""
                  src={
                    userInfo?.profilePic?.includes("s3")
                      ? userInfo?.profilePic
                      : ""
                  }
                  sx={{
                    width: 50,
                    height: 50,
                    ".MuiAvatar-img": { borderRadius: "50%" },
                    p: "2px",
                    border: `2px solid ${profileBorderColorRoleWise(userInfo?.userRole)}`,
                  }}
                />
              </IconButton>
            </div>
          </Grid2>
          <Grid2 container size={12} maxWidth={"100vw"}>
            <div
              style={{
                background: "var(--white)",
                display: "flex",
                maxWidth: "100%",
                minWidth: "100%",
              }}
            >
              <SideMenu menuList={items} />
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                style={{
                  flexGrow: 1,
                  minWidth: 0,
                  padding: disablePadding ? 0 : "16px",
                  maxHeight: "calc(100vh - 66px)",
                  minHeight: "calc(100vh - 66px)",
                  overflow: "auto !important",
                }}
                className="overFlowAuto"
              >
                {loading ? <SimpleLoader /> : children}
              </motion.div>
            </div>
          </Grid2>
        </Grid2>
      </Show.When>

      {Boolean(anchorEl) && (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Popper
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            transition
            placement="bottom-start"
            style={{
              zIndex: 5,
              width: `260px`,
              marginTop: "2px",
            }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Box
                  sx={{
                    borderRadius: "8px",
                    bgcolor: "var(--white)",
                    boxShadow: `4px 2px 8px 0px rgba(0, 0, 0, 0.13)`,
                    border: `1px solid`,
                    borderColor: "var(--neutral-100)",
                  }}
                >
                  <ProfileMenu
                    handleGetOPT={handleGetOPT}
                    otpLoading={otpLoading}
                  />
                </Box>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      )}

      {openPasswordModal && (
        <ChangePasswordModal
          open={openPasswordModal}
          setOpen={setOpenPasswordModal}
        />
      )}

      <Show.When isTrue={openAdvisorAddModal}>
        <AdvisorOnboarding
          open={openAdvisorAddModal}
          setOpen={setOpenAdvisorAddModal}
        />
      </Show.When>
    </>
  );
};

export default DashboardLayout;
